/**
 * Utility functions for generating user avatars and initials
 */

/**
 * Generate initials from a full name
 * @param {string} fullName - The full name to generate initials from
 * @returns {string} - The initials (e.g., "John Smith" -> "JS")
 */
export const getInitials = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return 'U';

  const names = fullName.trim().split(' ').filter(name => name.length > 0);

  if (names.length === 0) return 'U';

  // Leading punctuation is skipped, so a name that does not start with a letter still gets a
  // letter in the avatar. getMonogram below already did this for companies and the comment in
  // pages/Customers.js says the rule was "promoted to be the shared one" -- it was promoted to
  // getMonogram only, and people come through here, so a customer first-named
  // "<script>alert(1)</script>ORION" rendered as "<T": the angle bracket is charAt(0).
  const lettersOf = (word) => word.match(/[A-Za-z0-9]/g) || [];

  if (names.length === 1) {
    // If only one name, use its first two letters
    const only = lettersOf(names[0]);
    return only.length > 0 ? only.slice(0, 2).join('').toUpperCase() : 'U';
  }

  // Use first letter of first name and first letter of last name
  const firstInitial = lettersOf(names[0])[0] || '';
  const lastInitial = lettersOf(names[names.length - 1])[0] || '';
  const initials = (firstInitial + lastInitial).toUpperCase();
  return initials || 'U';
};

/**
 * Monogram for an ORGANISATION — a company, a customer record that may be a business, a vendor.
 *
 * This is a DIFFERENT fact from getInitials above, which is why it is a second function rather
 * than a merged one. A person's initials are first name + last name, so "John Robert Smith" is
 * JS and the middle name is skipped. A company's monogram is the leading letters in order, so
 * "Van der Berg-Quinn International Holdings, Ltd." is VD -- running getInitials over it gives
 * VL, pairing the V with "Ltd.", which is the least meaningful word in the name. Slack, Notion
 * and Linear all take the leading letters for a workspace or company. Collapsing the two would
 * have to get one of them wrong.
 *
 * The fallback is the caller's because it is the caller's context: a company card shows CO, a
 * customer or vendor row shows ?, and getInitials shows U for user. A shared default would put
 * "U" on a company avatar.
 *
 * Written from the copy in pages/Customers.js, which was the best of the five that existed --
 * it is the only one that skipped leading punctuation ("&Co Plumbing" -> CP, not &P) and the
 * only one that survived a whitespace-only name instead of rendering an empty circle. The
 * other four (Vendors and three CompanyProfile components) had both of those bugs; this is the
 * only behaviour that changes for them.
 *
 * @param {string} name
 * @param {string} [fallback] shown when the name yields no usable letters
 * @returns {string} at most two characters, uppercased
 */
export const getMonogram = (name, fallback = '?') => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return fallback;

  // First alphanumeric of each word, so a leading "&" or "(" does not become the monogram.
  const letters = words
    .map((w) => w.match(/[A-Za-z0-9]/)?.[0])
    .filter(Boolean);
  if (letters.length === 0) return fallback;

  return letters.slice(0, 2).join('').toUpperCase();
};

/**
 * Generate a consistent background color for an avatar based on the name
 * @param {string} name - The name to generate color from
 * @returns {string} - CSS classes for background and text color
 */
export const getAvatarColors = (name) => {
  if (!name) return 'bg-gray-100 text-gray-600';
  
  // Generate a hash from the name for consistent colors
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Define color combinations that work well
  const colorCombinations = [
    'bg-red-100 text-red-700',
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-yellow-100 text-yellow-700',
    'bg-purple-100 text-purple-700',
    'bg-pink-100 text-pink-700',
    'bg-indigo-100 text-indigo-700',
    'bg-orange-100 text-orange-700',
    'bg-teal-100 text-teal-700',
    'bg-cyan-100 text-cyan-700'
  ];
  
  const index = Math.abs(hash) % colorCombinations.length;
  return colorCombinations[index];
};

/**
 * Avatar component props generator
 * @param {string} name - The full name
 * @param {string} size - Size variant ('sm', 'md', 'lg', 'xl')
 * @returns {object} - Props for avatar component
 */
export const getAvatarProps = (name, size = 'md') => {
  const initials = getInitials(name);
  const colors = getAvatarColors(name);
  
  const sizeClasses = {
    sm: 'h-6 w-6 text-xs',
    md: 'h-8 w-8 text-sm',
    lg: 'h-10 w-10 text-sm',
    xl: 'h-12 w-12 text-lg'
  };
  
  return {
    initials,
    className: `${sizeClasses[size]} rounded-full ${colors} flex items-center justify-center font-medium`
  };
};

/**
 * React Avatar Component
 * @param {object} props - Component props
 * @param {string} props.name - The full name
 * @param {string} props.size - Size variant ('sm', 'md', 'lg', 'xl')
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} - Avatar component
 */
export const Avatar = ({ name, size = 'md', className = '' }) => {
  const { initials, className: avatarClassName } = getAvatarProps(name, size);
  
  return (
    <div className={`${avatarClassName} ${className}`}>
      <span>{initials}</span>
    </div>
  );
};

export default {
  getInitials,
  getAvatarColors,
  getAvatarProps,
  Avatar
};
